import { Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { useAdminNavBadges, useDeleteNavBadge } from "@/api/admin/nav-badges";
import { usePagination } from "@/api/pagination";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { navItemOptionLabel } from "@/components/sidebar/nav-item-options";
import { useCache } from "@/hooks/use-cache";
import { useSort } from "@/hooks/use-sort";
import { EditNavBadgeModal } from "@/pages/admin/modals/nav-badge-modal";
import { formatShortDate } from "@/pages/admin/tables/format-date";
import { success } from "@/providers/ToastProvider";

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

type BadgeStatus = "visible" | "hidden" | "expired";

const deriveStatus = (navBadge: { enabled: boolean; createdAt: string }): BadgeStatus => {
  if (!navBadge.enabled) return "hidden";
  const isExpired = Date.now() - new Date(navBadge.createdAt).getTime() > FOURTEEN_DAYS_MS;
  return isExpired ? "expired" : "visible";
};

const statusConfig: Record<BadgeStatus, { label: string; color: "gray" | "success" }> = {
  visible: { label: "Visible", color: "success" },
  hidden: { label: "Hidden", color: "gray" },
  expired: { label: "Expired", color: "gray" },
};

const StatusBadge = ({ navBadge }: { navBadge: { enabled: boolean; createdAt: string } }) => {
  const config = statusConfig[deriveStatus(navBadge)];
  return (
    <BadgeWithDot size="sm" color={config.color}>
      {config.label}
    </BadgeWithDot>
  );
};

const clearsOn = (createdAt: string) =>
  formatShortDate(new Date(new Date(createdAt).getTime() + FOURTEEN_DAYS_MS).toISOString());

const DeleteNavBadgeModal = ({ navBadge }: { navBadge: { id: string; navItemId: string } }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { mutateAsync } = useDeleteNavBadge();

  const onSubmit = async () => {
    await mutateAsync({ id: navBadge.id });
    success("Notification deleted");
    close();
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={close}>
      <ButtonUtility size="xs" color="tertiary" tooltip="Delete" icon={Trash01} onClick={() => setOpen(true)} />
      <ModalOverlay isDismissable>
        <Modal className="dark-mode max-w-md">
          <Dialog>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
              <CloseButton onClick={close} size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    Delete notification
                  </Heading>
                </div>
                <p>
                  Are you sure you want to delete the notification on &ldquo;{navItemOptionLabel(navBadge.navItemId)}
                  &rdquo;? Anyone who has not seen it yet never will. This action cannot be undone.
                </p>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={close}>
                  Cancel
                </Button>
                <Button color="primary-destructive" size="lg" type="button" onClick={onSubmit}>
                  Delete
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

export const AdminNavBadgesTable = () => {
  const pagination = usePagination();
  const sort = useSort<"createdAt" | "navItemId">("createdAt");
  const { data } = useAdminNavBadges(pagination.pagination, sort.sort);
  const total = useCache(data?.total);

  return (
    <TableCard.Root>
      <div className="overflow-auto">
        <Table
          aria-label="New page notifications"
          sortDescriptor={sort.descriptor}
          onSortChange={sort.setFromDescriptor}
        >
          <Table.Header>
            <Table.Head id="navItemId" label="Page" isRowHeader allowsSorting className="w-full" />
            <Table.Head id="createdAt" label="Live" allowsSorting className="w-[120px]" />
            <Table.Head id="clears" label="Clears" className="w-[120px]" />
            <Table.Head id="status" label="Status" className="w-[130px]" />
            <Table.Head id="actions" label="" className="w-min-0" />
          </Table.Header>

          <Table.Body items={data?.navBadges ?? []}>
            {(item) => (
              <Table.Row id={item.id}>
                <Table.Cell>
                  <p className="text-sm font-medium text-primary">{navItemOptionLabel(item.navItemId)}</p>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <Badge size="sm" color={statusConfig[deriveStatus(item)].color}>
                    {formatShortDate(item.createdAt)}
                  </Badge>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <p className="text-sm text-tertiary">{clearsOn(item.createdAt)}</p>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <StatusBadge navBadge={item} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <DeleteNavBadgeModal navBadge={item} />
                    <EditNavBadgeModal navBadge={item} />
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
    </TableCard.Root>
  );
};
